import axios from 'axios';
import type { AxiosResponse } from 'axios';
import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { apiConfig } from '../config/apiConfig';
import type { ScreenResponse } from '../types/ScreenResponse';

export const useScreen = () => {
  const [screen, setScreen] = useState<ScreenResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const loadScreen = useCallback(
    async (token: string, screenId: string) => {
      setIsLoading(true);
      setError(null);

      try {
        const screenData: AxiosResponse<ScreenResponse, any> =
          await axios.get<ScreenResponse>(
            apiConfig.screen.replace('{deeplink}', screenId),
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

        setScreen(screenData.data);

        return screenData.data;
      } catch (err) {
        const isAxiosError = axios.isAxiosError(err);
        const isTimeoutError =
          isAxiosError &&
          (err.code === 'ECONNABORTED' || err.message.includes('504'));

        const errorMessage = isAxiosError
          ? `Failed to load screen: ${err.message}`
          : 'Failed to load screen';

        setError(errorMessage);

        if (isTimeoutError) {
          navigate('/download-error');
        } else {
          navigate('/content-unavailable');
        }

        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [navigate]
  );

  const resetScreen = useCallback(() => {
    setScreen(null);
    setError(null);
    setIsLoading(false);
  }, []);

  return {
    screen,
    isLoading,
    error,
    loadScreen,
    resetScreen,
  };
};
